<template>
  <div class="pensionElements">
    <v-row>
      <v-col>
        <v-data-table
          :headers="headers"
          :items="pensionItems"
          dense
          :items-per-page="-1"
          :single-select="true"
          :calculate-widths="true"
          @click:row="editPensionElement"
        >
          <template v-slot:top>
            <v-dialog persistent v-model="isDialogShown" max-width="700">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="primary"
                  depressed
                  v-bind="attrs"
                  v-on="on"
                  :disabled="loading || !isEditMode"
                  :loading="loading"
                  loading-text="Loading members..."
                  class="mb-2"
                  @click="editPensionElement(defaultPensionElement)"
                >
                  Add row
                </v-btn>
              </template>

              <v-card>
                <v-card-title> Edit pension element </v-card-title>

                <v-card-text>
                  <v-container>
                    <v-form ref="form" v-model="isFormValid" lazy-validation>
                      <v-row>
                        <v-col cols="12" sm="6" md="4">
                          <v-select
                            v-model="editedPensionElement.Name"
                            label="Name"
                            :items="pensionElements"
                            item-text="Name"
                            item-value="Name"
                            :rules="rules"
                          ></v-select>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                          <v-text-field
                            v-model="editedPensionElement.Amount"
                            label="Amount"
                            type="number"
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                          <v-text-field
                            v-model="editedPensionElement.Cash"
                            label="Cash"
                            type="number"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                      <v-row class="baselignAlignment">
                        <v-col cols="12" sm="6" md="4">
                          <v-checkbox
                            v-model="editedPensionElement.Contingent"
                            label="Contingent"
                            disabled
                          ></v-checkbox>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                          <DatePicker
                            readOnly="false"
                            label="Effective date"
                            v-model="editedPensionElement.EffectiveDate"
                            :rules="rules"
                          ></DatePicker>
                        </v-col>
                      </v-row>
                    </v-form>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" outlined @click="isDialogShown = false"> Cancel </v-btn>
                  <v-btn
                    color="primary"
                    depressed
                    @click="savePensionElement"
                    :disabled="!isFormValid"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
          <template v-slot:[`item.Delete`]="{ item }">
            <v-icon :disabled="!isEditMode" @click.stop="onDelete(item)">mdi-delete</v-icon>
          </template>
          <template v-slot:[`item.Contingent`]="{ item }">
            <v-simple-checkbox v-model="item.Contingent" disabled></v-simple-checkbox>
          </template>
          <template v-slot:[`item.EffectiveDate`]="{ item }">
            <span>{{ displayDate(new Date(item.EffectiveDate)) }}</span>
          </template>
          <template v-slot:[`item.ExitDate`]="{ item }">
            <span>{{ displayDate(new Date(item.ExitDate)) }}</span>
          </template>
          <template v-slot:[`item.Amount`]="{ item }">
            <span>{{ Number(item.Amount).toFixed(2) }}</span>
          </template>
          <template v-slot:[`item.Cash`]="{ item }">
            <span>{{ Number(item.Cash).toFixed(2) }}</span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <Dialog ref="confirmationDialog" />
  </div>
</template>

<script>
import DatePicker from '@/components/formControls/DatePicker.vue';
import Utils from '@/utils';
import Dialog from '@/components/layout/Dialog.vue';

export default {
  name: 'PensionElements',
  components: { DatePicker, Dialog },
  props: {
    pensionItems: Array,
    currentPlanId: Number,
    selectedMemberId: Number,
    exitId: Number,
    isEditMode: Boolean,
  },

  data() {
    return {
      headers: [
        { text: '', value: 'Delete' },
        { text: 'Name', value: 'Name' },
        { text: 'Contingent', value: 'Contingent' },
        { text: 'Effective date', value: 'EffectiveDate' },
        { text: 'Amount', value: 'Amount', align: 'end' },
        { text: 'Cash', value: 'Cash', align: 'end' },
      ],
      pensionElements: {},
      isDialogShown: false,
      editedPensionElement: {},
      editedPensionIndex: -1,
      loading: false,
      defaultPensionElement: {
        Name: '',
        Contingent: false,
        ExitId: this.exitId,
        Gross: 0,
        Residual: 0,
        Commutation: 0,
        MembershipId: this.selectedMemberId,
        ElementId: 0,
        EffectiveDate: '',
        Amount: 0,
        Cash: 0,
        ExitDate: '',
      },
      isFormValid: false,
      rules: [(val) => !!val || 'Required'],
    };
  },

  computed: {
    editedElementName() {
      if (this.editedPensionElement.Name) return this.editedPensionElement.Name;
      return '';
    },
  },

  watch: {
    editedElementName() {
      if (this.editedPensionElement.Name) {
        const pensionElement = this.pensionElements.find(
          (el) => el.Name === this.editedPensionElement.Name
        );
        if (pensionElement) {
          this.editedPensionElement.Contingent = pensionElement.Contingent;
          this.editedPensionElement.ElementId = pensionElement.ElementId;
        }
      }
    },
  },

  created() {
    this.editedPensionElement = { ...this.defaultPensionElement };
  },

  mounted() {
    this.getPensionElements();
  },

  methods: {
    displayDate(date) {
      if (date.getFullYear() <= 1900) return null;
      return Utils.getDateStr(date);
    },

    resetPensionElement() {
      this.editedPensionElement = { ...this.defaultPensionElement };
      this.editedPensionIndex = -1;
      this.isDialogShown = false;
    },

    editPensionElement(item) {
      if (!this.isEditMode) return;
      if (this.pensionItems.length) {
        this.editedPensionIndex = this.pensionItems.indexOf(item);
      } else {
        this.editedPensionIndex = -1;
      }
      this.editedPensionElement = { ...item };
      this.isDialogShown = true;
    },

    savePensionElement() {
      this.isFormValid = this.$refs.form.validate();
      if (!this.isFormValid) return;
      this.editedPensionElement.MembershipId = this.selectedMemberId;
      if (this.editedPensionIndex === -1) {
        this.pensionItems.push(this.editedPensionElement);
      } else {
        this.pensionItems.splice(this.editedPensionIndex, 1, this.editedPensionElement);
      }
      this.resetPensionElement();
    },
    cancelChanges() {
      this.resetPensionElement();
    },

    deletePensionElement(elem) {
      const index = this.pensionItems.indexOf(elem);
      this.pensionItems.splice(index, 1);
    },

    async onDelete(elem) {
      console.log(elem);
      if (!this.isEditMode) return;
      try {
        await this.$refs.confirmationDialog.open(
          'Confirmation',
          'Are you sure you wish to delete this record?'
        );
        this.deletePensionElement(elem);
      } catch (err) {
        console.log();
      }
    },

    async getPensionElements() {
      this.loading = true;
      try {
        const response = await this.$api.get(
          '/pensionelements/GetPensionElements?planId=' + this.currentPlanId
        );

        this.pensionElements = response.data;
      } catch (error) {
        console.log(error.message);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.pensionElements {
  .v-data-table {
    max-width: 950px;
  }
}
</style>
